import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useLangLink } from '@/lib/langRouter';

export default function Footer({ onOpenCookies }) {
  const { t } = useTranslation();
  const l = useLangLink();

  return (
    <footer className="bg-[#0B1E3F] text-white pt-20 pb-10 mt-24 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E9B949]/60 to-transparent" />
      <div className="max-w-[1280px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-14 border-b border-white/10">
          <div className="md:col-span-4">
            <Link to={l('/')} className="flex items-center gap-2.5" data-testid="footer-logo">
              <svg width="40" height="40" viewBox="0 0 64 64">
                <rect width="64" height="64" rx="14" fill="#122B5C" />
                <rect x="14" y="36" width="8" height="18" fill="#E9B949" />
                <rect x="26" y="24" width="8" height="30" fill="#E9B949" />
                <rect x="38" y="14" width="8" height="40" fill="#E9B949" />
                <rect x="12" y="52" width="40" height="3" fill="#E9B949" />
              </svg>
              <div>
                <div className="font-serif text-[22px] font-semibold">Zyvanelix</div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-white/60">{t('footer.tagline')}</div>
              </div>
            </Link>
            <p className="mt-6 text-[13px] leading-relaxed text-white/60 max-w-sm">{t('footer.disclaimer')}</p>
          </div>

          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#E9B949] mb-4">{t('footer.navTitle')}</div>
            <ul className="space-y-2.5 text-[14px]">
              <li><a href={`${l('/')}#about`} className="text-white/80 hover:text-white transition" data-testid="footer-about">{t('nav.about')}</a></li>
              <li><a href={`${l('/')}#how`} className="text-white/80 hover:text-white transition" data-testid="footer-how">{t('nav.how')}</a></li>
              <li><a href={`${l('/')}#projects`} className="text-white/80 hover:text-white transition" data-testid="footer-projects">{t('nav.projects')}</a></li>
              <li><a href={`${l('/')}#contact`} className="text-white/80 hover:text-white transition" data-testid="footer-contact">{t('nav.contact')}</a></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#E9B949] mb-4">{t('footer.legalTitle')}</div>
            <ul className="space-y-2.5 text-[14px]">
              <li><Link to={l('/privacy')} className="text-white/80 hover:text-white transition" data-testid="footer-privacy">{t('footer.privacy')}</Link></li>
              <li><Link to={l('/cookies')} className="text-white/80 hover:text-white transition" data-testid="footer-cookies">{t('footer.cookies')}</Link></li>
              <li><Link to={l('/terms')} className="text-white/80 hover:text-white transition" data-testid="footer-terms">{t('footer.terms')}</Link></li>
              <li>
                <button onClick={onOpenCookies} className="text-white/80 hover:text-white transition underline underline-offset-2" data-testid="footer-cookie-settings">
                  {t('cookie.manage')}
                </button>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#E9B949] mb-4">{t('footer.companyTitle')}</div>
            <ul className="space-y-2 text-[13px] text-white/70">
              <li className="text-white font-medium">{t('footer.companyName')}</li>
              <li className="flex items-start gap-2"><MapPin size={13} className="mt-0.5 shrink-0 text-[#E9B949]" /><span>{t('footer.addressValue')}</span></li>
              <li className="flex items-start gap-2"><Mail size={13} className="mt-0.5 shrink-0 text-[#E9B949]" /><a href={`mailto:${t('footer.emailValue')}`} className="hover:text-white" data-testid="footer-email">{t('footer.emailValue')}</a></li>
              <li className="flex items-start gap-2"><Phone size={13} className="mt-0.5 shrink-0 text-[#E9B949]" /><a href={`tel:${t('footer.phoneValue').replace(/\s/g,'')}`} className="hover:text-white" data-testid="footer-phone">{t('footer.phoneValue')}</a></li>
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-6 py-10 text-[12px]">
          {[
            ['footer.regLabel', 'footer.regValue'],
            ['footer.taxLabel', 'footer.taxValue'],
            ['footer.licenseLabel', 'footer.licenseValue'],
            ['footer.jurisdictionLabel', 'footer.jurisdictionValue'],
            ['footer.incorporatedLabel', 'footer.incorporatedValue'],
            ['footer.capitalLabel', 'footer.capitalValue'],
          ].map(([lab, val]) => (
            <div key={lab}>
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t(lab)}</div>
              <div className="text-white/80">{t(val)}</div>
            </div>
          ))}
        </div>

        <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-[12px] text-white/50">
          <div>{t('footer.copyright')}</div>
          <div className="flex items-center gap-4">
            <a href="/sitemap.xml" className="hover:text-white/80">sitemap.xml</a>
            <a href="/robots.txt" className="hover:text-white/80">robots.txt</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
